import type { LocationQueryRaw } from 'vue-router'
import { TOCCO_EVENT_ENVIRONMENTS } from './useToccoEvents'

export type ToccoEventSearchRouterValue = string | string[] | undefined
export type ToccoEventSearchRouteQuery = LocationQueryRaw

export const TYPE_GROUPS = [
  { key: 'seminar', types: ['Lehrgang'] },
  { key: 'module', types: ['Modul', 'Einführungsmodul'] },
  { key: 'course', types: ['Kurs'] },
  { key: 'webinar', types: ['Webinar'] },
] as const

export const TOCCO_EVENT_TYPE_KEYS = TYPE_GROUPS.map(({ key }) => key)
export const TOCCO_EVENT_METHOD_KEYS = [...TOCCO_EVENT_ENVIRONMENTS]

const parseSelection = (value: ToccoEventSearchRouterValue, optionKeys: string[]) => {
  const serialized = Array.isArray(value) ? value.join(',') : (value ?? '')
  const tokens = new Set(serialized.split(',').filter(Boolean))
  const selection = optionKeys.filter((key) => tokens.has(key))

  return selection.length ? selection : [...optionKeys]
}

const serializeSelection = (selection: readonly string[], optionKeys: string[]) => {
  const selected = new Set(selection)
  const canonicalSelection = optionKeys.filter((key) => selected.has(key))

  if (!canonicalSelection.length || canonicalSelection.length === optionKeys.length) return
  return canonicalSelection.join(',')
}

export const parseTypeParam = (value: ToccoEventSearchRouterValue) => parseSelection(value, TOCCO_EVENT_TYPE_KEYS)

export const serializeTypeSelection = (selection: readonly string[]) =>
  serializeSelection(selection, TOCCO_EVENT_TYPE_KEYS)

export const parseMethodParam = (value: ToccoEventSearchRouterValue) => parseSelection(value, TOCCO_EVENT_METHOD_KEYS)

export const serializeMethodSelection = (selection: readonly string[]) =>
  serializeSelection(selection, TOCCO_EVENT_METHOD_KEYS)

const normalizeQueryValue = (value: unknown) => {
  if (Array.isArray(value)) return value.map((item) => item?.toString() ?? '').join(',')
  return value?.toString() ?? ''
}

export const typeSelectionMatchesParam = (selection: readonly string[], value: ToccoEventSearchRouterValue) =>
  serializeTypeSelection(selection) === serializeTypeSelection(parseTypeParam(value))

export const methodSelectionMatchesParam = (selection: readonly string[], value: ToccoEventSearchRouterValue) =>
  serializeMethodSelection(selection) === serializeMethodSelection(parseMethodParam(value))

export const parseToccoEventSearchFilters = ({
  type,
  method,
}: {
  type: ToccoEventSearchRouterValue
  method: ToccoEventSearchRouterValue
}) => ({
  typeSelection: parseTypeParam(type),
  methodSelection: parseMethodParam(method),
})

export const buildToccoEventSearchQuery = ({
  q,
  typeSelection,
  methodSelection,
}: {
  q?: string
  typeSelection: readonly string[]
  methodSelection: readonly string[]
}) => {
  const type = serializeTypeSelection(typeSelection)
  const method = serializeMethodSelection(methodSelection)

  return {
    ...(q !== undefined ? { q } : {}),
    ...(type ? { type } : {}),
    ...(method ? { method } : {}),
  }
}

export const buildToccoEventFilterRouteQuery = ({
  currentQuery,
  q,
  typeSelection,
  methodSelection,
}: {
  currentQuery: ToccoEventSearchRouteQuery
  q?: string
  typeSelection: readonly string[]
  methodSelection: readonly string[]
}) => {
  const query = { ...currentQuery }
  delete query.type
  delete query.method

  return Object.assign(query, buildToccoEventSearchQuery({ q, typeSelection, methodSelection }))
}

export const toccoEventSearchQueriesMatch = (current: ToccoEventSearchRouteQuery, next: ToccoEventSearchRouteQuery) => {
  const currentKeys = Object.keys(current)
  const nextKeys = Object.keys(next)

  return (
    currentKeys.length === nextKeys.length &&
    currentKeys.every(
      (key) => Object.hasOwn(next, key) && normalizeQueryValue(current[key]) === normalizeQueryValue(next[key]),
    )
  )
}

export const getToccoEventResultsCountKey = (q: string) =>
  q.trim() ? 'tocco.eventSearch.resultsCount' : 'tocco.eventSearch.resultsCountAll'
